export const computeTangentAngle = (p0, p1) => {
  if (!p0 || !p1) return 0;
  const dx = p1[0] - p0[0];
  const dy = p1[1] - p0[1];
  if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) {
    return 0;
  }
  return Math.atan2(dy, dx);
};

const distanceSq = (p0, p1) => {
  const dx = p1[0] - p0[0];
  const dy = p1[1] - p0[1];
  return Math.sqrt(dx * dx + dy * dy);
};

const toCartesian = (lonDeg, latDeg) => {
  const rad = Math.PI / 180;
  const lon = lonDeg * rad;
  const lat = latDeg * rad;
  const cosLat = Math.cos(lat);
  return [
    cosLat * Math.cos(lon),
    cosLat * Math.sin(lon),
    Math.sin(lat),
  ];
};

const toLonLat = (x, y, z) => {
  const deg = 180 / Math.PI;
  const lat = Math.atan2(z, Math.hypot(x, y)) * deg;
  const lon = Math.atan2(y, x) * deg;
  return [lon, lat];
};

const buildFlightArcSegment = (p0, p1, steps = 40) => {
  const v0 = toCartesian(p0[0], p0[1]);
  const v1 = toCartesian(p1[0], p1[1]);
  const dot = Math.max(-1, Math.min(1, v0[0] * v1[0] + v0[1] * v1[1] + v0[2] * v1[2]));
  const omega = Math.acos(dot);
  const sinOmega = Math.sin(omega);

  if (omega < 1e-6 || sinOmega < 1e-6) {
    return buildDirectSegment(p0, p1, steps);
  }

  const segment = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const k0 = Math.sin((1 - t) * omega) / sinOmega;
    const k1 = Math.sin(t * omega) / sinOmega;
    const x = k0 * v0[0] + k1 * v1[0];
    const y = k0 * v0[1] + k1 * v1[1];
    const z = k0 * v0[2] + k1 * v1[2];
    segment.push(toLonLat(x, y, z));
  }
  return segment;
};

const buildDirectSegment = (p0, p1, steps = 10) => {
  const segment = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    segment.push([
      p0[0] + (p1[0] - p0[0]) * t,
      p0[1] + (p1[1] - p0[1]) * t,
    ]);
  }
  return segment;
};

const buildSeaSegment = (p0, p1) => {
  const steps = 30;
  const segment = [];
  const dx = p1[0] - p0[0];
  const dy = p1[1] - p0[1];
  const normalX = -dy * 0.15;
  const normalY = dx * 0.15;

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const arcHeight = Math.sin(t * Math.PI);
    segment.push([
      p0[0] + dx * t + normalX * arcHeight,
      p0[1] + dy * t + normalY * arcHeight,
    ]);
  }
  return segment;
};

const buildTrekSegment = (p0, p1) => {
  const steps = 16;
  const segment = [];
  const dx = p1[0] - p0[0];
  const dy = p1[1] - p0[1];

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const jitter = Math.sin(t * Math.PI * 3) * 0.02;
    segment.push([
      p0[0] + dx * t - dy * jitter,
      p0[1] + dy * t + dx * jitter,
    ]);
  }
  return segment;
};

const getSegmentForStyle = (p0, p1, routeStyle) => {
  switch (routeStyle) {
    case "sea":
      return buildSeaSegment(p0, p1);
    case "trek":
      return buildTrekSegment(p0, p1);
    case "direct":
      return buildDirectSegment(p0, p1);
    case "flight":
    default:
      return buildFlightArcSegment(p0, p1);
  }
};

const extractCoord = (wp) => {
  if (!wp) return null;
  if (Array.isArray(wp.coordinate) && wp.coordinate.length >= 2) return wp.coordinate;
  if (Array.isArray(wp) && wp.length >= 2) return wp;
  return null;
};

export const buildMultiSegmentRoute = (waypoints = [], routeStyle = "flight") => {
  if (!Array.isArray(waypoints) || waypoints.length < 2) {
    return [];
  }

  const allCoords = [];
  for (let i = 0; i < waypoints.length - 1; i++) {
    const p0 = extractCoord(waypoints[i]);
    const p1 = extractCoord(waypoints[i + 1]);
    if (!p0 || !p1) continue;

    const segment = getSegmentForStyle(p0, p1, routeStyle);
    if (segment.length === 0) continue;

    if (allCoords.length === 0) {
      allCoords.push(...segment);
    } else {
      allCoords.push(...segment.slice(1));
    }
  }

  return allCoords;
};

export const splitRouteAtProgress = (coords = [], progress = 0) => {
  if (!Array.isArray(coords) || coords.length === 0) {
    return {
      completedCoords: [],
      upcomingCoords: [],
      cometCoord: null,
      cometAngleRad: 0,
    };
  }

  if (coords.length === 1) {
    return {
      completedCoords: [[...coords[0]]],
      upcomingCoords: [],
      cometCoord: [...coords[0]],
      cometAngleRad: 0,
    };
  }

  const clampedP = Math.max(0, Math.min(1, progress));

  if (clampedP === 0) {
    const startPoint = coords[0];
    const nextPoint = coords[1] || startPoint;
    return {
      completedCoords: [startPoint],
      upcomingCoords: [...coords],
      cometCoord: startPoint,
      cometAngleRad: computeTangentAngle(startPoint, nextPoint),
    };
  }

  if (clampedP === 1) {
    const endPoint = coords[coords.length - 1];
    const prevPoint = coords[coords.length - 2] || endPoint;
    return {
      completedCoords: [...coords],
      upcomingCoords: [endPoint],
      cometCoord: endPoint,
      cometAngleRad: computeTangentAngle(prevPoint, endPoint),
    };
  }

  // Compute segment lengths
  let totalLength = 0;
  const segLengths = [];
  for (let i = 0; i < coords.length - 1; i++) {
    const dist = distanceSq(coords[i], coords[i + 1]);
    segLengths.push(dist);
    totalLength += dist;
  }

  const targetDist = clampedP * totalLength;
  let accumulated = 0;
  let segIndex = 0;
  let localT = 0;

  for (let i = 0; i < segLengths.length; i++) {
    const len = segLengths[i];
    if (accumulated + len >= targetDist || i === segLengths.length - 1) {
      segIndex = i;
      localT = len > 0 ? (targetDist - accumulated) / len : 0;
      break;
    }
    accumulated += len;
  }

  const p0 = coords[segIndex];
  const p1 = coords[segIndex + 1];
  const cometCoord = [
    p0[0] + (p1[0] - p0[0]) * localT,
    p0[1] + (p1[1] - p0[1]) * localT,
  ];

  const cometAngleRad = computeTangentAngle(p0, p1);
  const completedCoords = [...coords.slice(0, segIndex + 1), cometCoord];
  const upcomingCoords = [cometCoord, ...coords.slice(segIndex + 1)];

  return {
    completedCoords,
    upcomingCoords,
    cometCoord,
    cometAngleRad,
  };
};

const resolveWaypointCoords = (waypoints) => {
  if (!Array.isArray(waypoints)) return [];
  const list = [];
  for (let i = 0; i < waypoints.length; i++) {
    const c = extractCoord(waypoints[i]);
    if (c) list.push(c);
  }
  return list;
};

const computeActiveSegment = (segmentCount, progress) => {
  const clampedP = Math.max(0, Math.min(1, progress));
  if (clampedP >= 1) {
    return { activeSegIdx: segmentCount - 1, localT: 1 };
  }
  const rawIndex = clampedP * segmentCount;
  const activeSegIdx = Math.min(segmentCount - 1, Math.floor(rawIndex));
  return { activeSegIdx, localT: rawIndex - activeSegIdx };
};

const assembleCompletedCoords = (segments, activeSegIdx, activeCompleted) => {
  const completed = [];
  for (let i = 0; i < activeSegIdx; i++) {
    const seg = segments[i];
    const sliceIdx = completed.length === 0 ? 0 : 1;
    completed.push(...seg.slice(sliceIdx));
  }
  const tailSlice = completed.length === 0 ? 0 : 1;
  completed.push(...activeCompleted.slice(tailSlice));
  return completed;
};

const assembleUpcomingCoords = (segments, activeSegIdx, activeUpcoming) => {
  const upcoming = [...activeUpcoming];
  for (let i = activeSegIdx + 1; i < segments.length; i++) {
    upcoming.push(...segments[i].slice(1));
  }
  return upcoming;
};

export const splitMultiSegmentRoute = (waypoints = [], routeStyle = "flight", progress = 0) => {
  const coordsList = resolveWaypointCoords(waypoints);
  if (coordsList.length < 2) {
    return {
      completedCoords: [],
      upcomingCoords: [],
      cometCoord: null,
      cometAngleRad: 0,
    };
  }

  const segmentCount = coordsList.length - 1;
  const { activeSegIdx, localT } = computeActiveSegment(segmentCount, progress);

  const segments = [];
  for (let i = 0; i < segmentCount; i++) {
    segments.push(getSegmentForStyle(coordsList[i], coordsList[i + 1], routeStyle));
  }

  const activeSplit = splitRouteAtProgress(segments[activeSegIdx], localT);
  const completedCoords = assembleCompletedCoords(segments, activeSegIdx, activeSplit.completedCoords);
  const upcomingCoords = assembleUpcomingCoords(segments, activeSegIdx, activeSplit.upcomingCoords);

  return {
    completedCoords,
    upcomingCoords,
    cometCoord: activeSplit.cometCoord,
    cometAngleRad: activeSplit.cometAngleRad,
  };
};

